import { t } from 'i18next'
import { FC, useMemo, useState } from 'react'

import SmartToyIcon from '@mui/icons-material/SmartToy'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material'

import AIAvatar from '../../../../../components/AIAvatar/AIAvatar'
import GRDialogTitle, { GRDialogCloseButton } from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { GRMarkdown } from '../../../../../components/GRMarkdown/GRMarkdown'
import { useGetLiveEventAISummaryForCustomPromptQuery } from '../../../../../internal/api/live-event-predictions'
import { cuteRoboticThoughts } from '../../../../../pages/AIAnalyst/AIAnalystPageInfo'
import { GameIcon } from '../../../../game'
import { TrackedGame } from '../../../types/TrackedGame'

type LiveEventGameAIButtonPromptDialogProps = DialogProps & {
  data: LiveEventGameAIButtonPromptDialogData
}

export type LiveEventGameAIButtonPromptDialogData = {
  selectedTrackedGame: TrackedGame
  trackedGames: TrackedGame[]
}

const reasoningLevels = ['low', 'medium', 'high']

/* This component is purely for GameRefinery Internal use for AI Prompts testing. This component contains non translated texts because of that. */

export const LiveEventGameAIButtonPromptDialog: FC<LiveEventGameAIButtonPromptDialogProps> = ({
  open,
  onClose,
  data: { selectedTrackedGame, trackedGames },
}) => {
  const [selectedGameId, setSelectedGameId] = useState<string>(selectedTrackedGame.game.id)
  const [currentLiveEventAIPrompt, setCurrentLiveEventAIPrompt] = useState<string>(`How has the game's live events strategy changed in past month?`)
  const [selectedLiveEventAIPrompt, setSelectedLiveEventAIPrompt] = useState<string>('')
  const [selectedMonthsAgo, setSelectedMonthsAgo] = useState<number>(1)
  const [selectedReasoningLevel, setSelectedReasoningLevel] = useState<'low' | 'medium' | 'high'>('low')
  const [usePythonInterpreter, setUsePythonInterpreter] = useState<boolean>(true)

  const { data: aiResponse, isFetching: isFetchingAIResponse } = useGetLiveEventAISummaryForCustomPromptQuery(
    {
      gameId: selectedGameId,
      prompt: selectedLiveEventAIPrompt,
      months: selectedMonthsAgo,
      reasoningLevel: selectedReasoningLevel,
      interpreter: usePythonInterpreter,
    },
    { skip: !selectedGameId || !selectedLiveEventAIPrompt || !open }
  )

  const aiLoadingText = useMemo(() => {
    return cuteRoboticThoughts[Math.floor(Math.random() * cuteRoboticThoughts.length)]
  }, [])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableRestoreFocus
      className="LiveEventGameAIButtonPromptDialog"
      PaperProps={{ sx: { minHeight: '90vh', maxHeight: '90vh' } }}
    >
      <GRDialogTitle>
        <Grid container alignItems="center">
          <Grid item xs={3}>
            {' '}
          </Grid>
          <Grid item xs={6} container direction="column" alignItems="center">
            {t('common:custom_ai_prompt')}
          </Grid>
          <Grid item xs={3} container direction="column" alignItems="flex-end">
            <GRDialogCloseButton onClick={onClose} share={false} />
          </Grid>
        </Grid>
      </GRDialogTitle>

      <Divider />

      <DialogContent>
        <Grid container spacing={2} wrap="wrap">
          <Grid item xs={12}>
            <FormControl size="small" variant="outlined" disabled={isFetchingAIResponse} fullWidth>
              <InputLabel htmlFor="game-id-selector-label">{t('compare:select_game')}</InputLabel>
              <Select
                labelId="game-id-selector-label"
                size="small"
                value={selectedGameId}
                onChange={(e) => setSelectedGameId(e.target.value)}
                label={t('compare:select_game')}
              >
                {trackedGames.map((trackedGame) => {
                  return (
                    <MenuItem key={trackedGame.game.id} value={trackedGame.game.id}>
                      <Grid container spacing={1}>
                        <Grid item>
                          <div style={{ display: 'block', height: '16px' }}>
                            <GameIcon
                              src={trackedGame.game.icons || trackedGame.game.icon}
                              gameName={trackedGame.game.resolvedName}
                              size="xtiny"
                              discontinued={trackedGame.discontinued}
                            />
                          </div>
                        </Grid>
                        <Grid item>{trackedGame.game.name}</Grid>
                      </Grid>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="custom-prompt-input"
              label={t('common:custom_ai_prompt')}
              size="small"
              disabled={isFetchingAIResponse}
              fullWidth
              multiline
              minRows={4}
              value={currentLiveEventAIPrompt}
              onChange={(e) => setCurrentLiveEventAIPrompt(e.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs={'auto'}>
            <LoadingButton
              variant="contained"
              startIcon={<SmartToyIcon />}
              disabled={isFetchingAIResponse || !currentLiveEventAIPrompt || selectedLiveEventAIPrompt === currentLiveEventAIPrompt}
              loadingPosition="start"
              loading={isFetchingAIResponse}
              onClick={() => setSelectedLiveEventAIPrompt(currentLiveEventAIPrompt)}
            >
              {t('common:send_prompt_to_ai')}
            </LoadingButton>
          </Grid>
          <Grid item xs={2}>
            <TextField
              id="custom-prompt-input"
              label={t('common:months_ago')}
              type="number"
              size="small"
              disabled={isFetchingAIResponse}
              value={selectedMonthsAgo}
              onChange={(e) => {
                setSelectedLiveEventAIPrompt('')
                setSelectedMonthsAgo(Number(e.target.value))
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <FormControl size="small" variant="outlined" disabled={isFetchingAIResponse} fullWidth>
              <InputLabel htmlFor="reasoning-level-selector-label">{t('common:ai_reasoning_level')}</InputLabel>
              <Select
                labelId="reasoning-level-selector-label"
                size="small"
                value={selectedReasoningLevel}
                onChange={(e) => {
                  setSelectedLiveEventAIPrompt('')
                  setSelectedReasoningLevel(e.target.value as 'low' | 'medium' | 'high')
                }}
                label={t('common:ai_reasoning_level')}
              >
                {reasoningLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    <span>{t(`common:ai_reasoning_level__${level}`)}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  disabled={true}
                  checked={usePythonInterpreter}
                  onChange={() => {
                    setSelectedLiveEventAIPrompt('')
                    setUsePythonInterpreter(!usePythonInterpreter)
                  }}
                />
              }
              label={t('common:use_python_interpreter')}
            />
          </Grid>
        </Grid>

        {(aiResponse || isFetchingAIResponse) && (
          <>
            <Box my={2}>
              <Divider />
            </Box>

            <Box sx={{ padding: '5px 20px', backgroundColor: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '20px' }}>
              {!isFetchingAIResponse && aiResponse && <GRMarkdown text={aiResponse} />}

              {isFetchingAIResponse && (
                <>
                  <Box my={3} textAlign={'center'}>
                    <AIAvatar className="AnimateAvatar" />
                    <Box mt={1} color="#555">
                      {aiLoadingText}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
